import {
  Checkbox,
  CheckboxGroup,
  Flex,
  FormControl,
  FormLabel,
  NumberInput,
  NumberInputField,
  Text,
} from "@chakra-ui/react";
import { FormattedMessage, useIntl } from "react-intl";

import { COMPOSITE_MAPS, CompositeMapConfig } from "@nens/ldo/api-v1";

interface ExportCompositeMapsFieldsProps {
  readonly: boolean;
  value: CompositeMapConfig;
  onChange: (e: Partial<CompositeMapConfig>) => void;
  formErrorKeys: string[];
}

export function ExportCompositeMapsFields({
  readonly,
  value,
  onChange,
  formErrorKeys,
}: ExportCompositeMapsFieldsProps) {
  const intl = useIntl();

  const COMPOSITE_MAP_DESCRIPTIONS: Record<string, string> = {
    max_velocity: intl.formatMessage({
      id: "composite_map.max_velocity",
      defaultMessage: "Maximum velocity",
    }),
    max_waterdepth: intl.formatMessage({
      id: "composite_map.max_waterdepth",
      defaultMessage: "Maximum water depth",
    }),
    max_waterlevel: intl.formatMessage({
      id: "composite_map.max_waterlevel",
      defaultMessage: "Maximum water level",
    }),
    arrival_times: intl.formatMessage({
      id: "composite_map.arrival_times",
      defaultMessage: "Arrival times",
    }),
    rate_of_rise: intl.formatMessage({
      id: "composite_map.rate_of_rise",
      defaultMessage: "Rate of rise",
    }),
    rise_period: intl.formatMessage({
      id: "composite_map.rise_period",
      defaultMessage: "Rise period",
    }),
    inundation_sources: intl.formatMessage({
      id: "composite_map.inundation_sources",
      defaultMessage: "Inundation sources",
    }),
  };

  return (
    <Flex
      flexDirection={{
        base: "column",
        md: "row",
      }}
    >
      <FormControl my="4" border="1px solid #E2E8F0" rounded="xl" p="2">
        <FormLabel fontWeight="semibold">
          <FormattedMessage
            id="pages.export_scenarios.new.cell_size"
            defaultMessage="Raster cell size"
          />
        </FormLabel>
        <NumberInput
          defaultValue={value.cell_size}
          min={5}
          max={100000}
          precision={0}
          keepWithinRange={true}
          clampValueOnBlur={true}
          isReadOnly={readonly}
          onChange={(asString, asNumber) => {
            if (!isNaN(asNumber) && asNumber !== value.cell_size && asNumber >= 5) {
              onChange({ cell_size: asNumber });
            }
          }}
        >
          <NumberInputField />
        </NumberInput>
      </FormControl>
      <FormControl my="4" border="1px solid #E2E8F0" rounded="xl" p="2">
        <FormLabel fontWeight="semibold">
          <FormattedMessage
            id="pages.export_scenarios.new.composite_raster_types"
            defaultMessage="Raster types"
          />
        </FormLabel>
        <CheckboxGroup
          value={value.raster_types}
          onChange={(e) =>
            readonly || onChange({ raster_types: e as CompositeMapConfig["raster_types"] })
          }
        >
          <Flex flexDirection="column">
            {COMPOSITE_MAPS.map((compositeMap) => (
              <Checkbox key={compositeMap} value={compositeMap} isReadOnly={readonly}>
                {COMPOSITE_MAP_DESCRIPTIONS[compositeMap] ?? compositeMap}
              </Checkbox>
            ))}
          </Flex>
        </CheckboxGroup>
        {formErrorKeys.includes("raster_types") ? (
          <Text color="red.500">
            <FormattedMessage
              id="pages.export_scenarios.new.composite_raster_types_error"
              defaultMessage="Please select at least one raster type"
            />
          </Text>
        ) : null}
      </FormControl>
    </Flex>
  );
}
